export enum SigningOriginType {
  CANDIDATE = 'candidate',
  CONTACT = 'contact',
  COMPANY = 'company',
  JOB = 'job',
  PLACEMENT = 'placement',
  APPLICATION = 'application',
  OPPORTUNITY = 'opportunity',
}

export enum SigningMode {
  PARALLEL = 'parallel',
  SEQUENTIAL = 'sequential',
}

export enum SigningStatus {
  DRAFT = 'draft',
  SENT = 'sent',
  VIEWED = 'viewed',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  VOIDED = 'voided',
}

export enum SignerStatus {
  SENT = 'sent',
  DELIVERED = 'delivered',
  OPENED = 'opened',
  SIGNED = 'signed',
  DECLINED = 'declined',
  DELIVERY_FAILED = 'delivery_failed',
}

export enum SigningFieldType {
  SIGNATURE = 'signature',
  INITIALS = 'initials',
  DATE_SIGNED = 'date_signed',
  FULL_NAME = 'full_name',
  EMAIL = 'email',
  COMPANY_NAME = 'company_name',
  TEXT = 'text',
  MULTILINE = 'multiline',
  NUMBER = 'number',
  CURRENCY = 'currency',
  DATE = 'date',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
  DROPDOWN = 'dropdown',
  STATIC_TEXT = 'static_text',
  MERGE = 'merge',
}

export interface SigningFieldInput {
  type: SigningFieldType;
  signer_id?: string | null;
  page_index: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required?: boolean;
  placeholder?: string;
  value?: string;
  merge_source?: string;
  overflow_strategy?: 'shrink' | 'truncate' | 'wrap';
}

export interface SignerInput {
  first_name: string;
  last_name: string;
  email: string;
  organisation?: string;
  role?: string;
  signing_order?: number;
  is_sender?: boolean;
  contact_id?: string | null;
  participation?: 'sign' | 'view_only' | 'approve_only';
  message?: string;
}

export interface CreateSigningRequestInput {
  name: string;
  document_type?: string;
  signing_mode?: SigningMode;
  origin_entity_type: SigningOriginType;
  origin_entity_id: string;
  due_date?: string;
  expiry_date?: string;
}

export interface SigningSigner {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
  signing_order: number;
  is_sender: boolean;
  contact_id?: string | null;
  status: SignerStatus;
  access_token?: string;
  opened_at?: string | null;
  signed_at?: string | null;
}

export interface SigningField {
  id: string;
  type: SigningFieldType;
  page_index: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  placeholder?: string;
  value?: string;
  signer?: { id: string } | null;
}

export interface SigningRequest {
  id: string;
  name: string;
  status: SigningStatus;
  signing_mode: SigningMode;
  origin_entity_type: SigningOriginType;
  origin_entity_id: string;
  email_subject?: string;
  email_message?: string;
  sent_at?: string | null;
  hashed_at?: string | null;
  document_sha256?: string | null;
  source_file?: { id: string; public_path: string } | null;
  completed_file?: { id: string; public_path: string } | null;
  certificate_file?: { id: string; public_path: string } | null;
  signers: SigningSigner[];
  fields: SigningField[];
}

export interface PublicSignerView {
  document: {
    id: string;
    name: string;
    status: SigningStatus;
    source_file_url: string | null;
  };
  signer: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    status: SignerStatus;
    consent_given: boolean;
  };
  fields: Array<{
    id: string;
    type: SigningFieldType;
    page_index: number;
    x: number;
    y: number;
    width: number;
    height: number;
    required: boolean;
    placeholder?: string;
    value?: string;
    assigned_to_me: boolean;
  }>;
}

export interface SigningAdminRow {
  id: string;
  request_id: string;
  sender_name: string;
  file_name: string;
  recipient_name: string;
  recipient_email: string;
  is_sender: boolean;
  /** TESTING/STAGING ONLY — tokenised signer link; remove before production. */
  access_token?: string;
  document_status: SigningStatus;
  signer_status: SignerStatus;
  sent_at?: string | null;
  reviewed_at?: string | null;
  signed_at?: string | null;
  completed_at?: string | null;
  completed_file_url?: string | null;
  certificate_url?: string | null;
}

export interface SubmitSigningInput {
  token: string;
  field_values?: { field_id: string; value?: string }[];
  signature_image?: string;
}
